import { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { importApi } from '../api/imports'
import { settingsApi } from '../api/settings'
import { ApiError } from '../api/client'
import type {
  EntityDef,
  MappingColumn,
  TenantSettings,
  UploadResult,
  ValidationReport,
} from '../api/types'
import { ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { onSettingsSaved } from '../pages/settings/settingsEvents'
import { ImportWizardContext } from './ImportWizardContext'
import type { ImportWizard } from './ImportWizardContext'

/**
 * Where the chosen entity type is stashed so it survives a full-page navigation.
 *
 * The Google Sheets consent flow leaves the SPA entirely (`window.location.assign`
 * to Google, then a redirect back), so every in-memory field here is gone by the
 * time step 2 renders again. Step 1's choice is the only one that has to outlive
 * that: without it the page's own guard sends the returning user to /app/import
 * and the consent work looks like it did nothing. `sessionStorage` rather than
 * `localStorage` so closing the tab still clears the draft, matching the
 * in-memory lifetime of everything else in this provider.
 */
export const ENTITY_TYPE_KEY = 'bb.import.entityType'

/**
 * Holds the five-call import chain's state between steps. Everything lives in
 * memory except the entity type, which is persisted (see above) because the
 * Google consent round trip remounts the whole tree.
 */
export function ImportWizardProvider() {
  const entities = useAsync<EntityDef[]>(() => importApi.entities(), [])
  const settingsState = useAsync<TenantSettings>(() => settingsApi.get(), [])
  const [entityType, setEntityTypeState] = useState(
    () => sessionStorage.getItem(ENTITY_TYPE_KEY) ?? '',
  )
  const [upload, setUploadState] = useState<UploadResult | null>(null)
  const [sheet, setSheetState] = useState<string | null>(null)
  const [columns, setColumns] = useState<MappingColumn[]>([])
  const [unmapped, setUnmapped] = useState<string[]>([])
  const [validation, setValidation] = useState<ValidationReport | null>(null)
  const [saveMappingAs, setSaveMappingAs] = useState('')
  const [options, setOptions] = useState<Record<string, string> | null>(null)

  const settings = settingsState.data ?? null
  const settingsError = settingsState.error
    ? settingsState.error instanceof ApiError
      ? settingsState.error.message
      : 'Could not load your import settings.'
    : null

  // Everything derived from the settings has to go when they change: a preview
  // validated under the old date format is not a preview of the new one, and
  // submitting it would send rows the user never saw validated.
  const reloadSettings = useCallback(() => {
    setValidation(null)
    settingsState.reload()
  }, [settingsState])

  /* A save from the Settings dialog invalidates the preview.
   *
   * The report on the Review step is computed from the tenant's settings, so a
   * user who turned on "create missing items" *because* the preview said the
   * item was not found would otherwise close the dialog and still be looking at
   * the report that made them open it. Subscribing here — rather than calling
   * back through the shell — is what makes the fix visible without leaving and
   * re-entering the wizard. */
  useEffect(() => onSettingsSaved(reloadSettings), [reloadSettings])

  // Changing the sheet invalidates the mapping — the new sheet has different
  // headers, so columns from the old sheet are meaningless.
  const setSheet = useCallback((name: string) => {
    setSheetState(name)
    setColumns([])
    setUnmapped([])
    setValidation(null)
  }, [])

  // Changing the entity invalidates the mapping and anything derived from it.
  const setEntityType = useCallback((name: string) => {
    setEntityTypeState(name)
    if (name) {
      sessionStorage.setItem(ENTITY_TYPE_KEY, name)
    } else {
      sessionStorage.removeItem(ENTITY_TYPE_KEY)
    }
    setColumns([])
    setUnmapped([])
    setValidation(null)
  }, [])

  const setUpload = useCallback((next: UploadResult | null) => {
    setUploadState(next)
    setSheetState(next?.sheets[0] ?? null)
    setColumns([])
    setUnmapped([])
    setValidation(null)
  }, [])

  const reset = useCallback(() => {
    setEntityTypeState('')
    sessionStorage.removeItem(ENTITY_TYPE_KEY)
    setUploadState(null)
    setSheetState(null)
    setColumns([])
    setUnmapped([])
    setValidation(null)
    setSaveMappingAs('')
    setOptions(null)
  }, [])

  const list = useMemo(() => entities.data ?? [], [entities.data])
  const value = useMemo<ImportWizard>(
    () => ({
      entities: list,
      entityType,
      entity: list.find((e) => e.name === entityType) ?? null,
      upload,
      sheet,
      columns,
      unmapped,
      validation,
      saveMappingAs,
      options,
      setOptions,
      settings,
      settingsError,
      reloadSettings,
      setEntityType,
      setUpload,
      setSheet,
      setColumns,
      setUnmapped,
      setValidation,
      setSaveMappingAs,
      reset,
    }),
    [
      list,
      entityType,
      upload,
      sheet,
      columns,
      unmapped,
      validation,
      saveMappingAs,
      options,
      settings,
      settingsError,
      reloadSettings,
      setEntityType,
      setUpload,
      reset,
    ],
  )

  if (entities.loading) return <LoadingState label="Loading import types…" />
  if (entities.error) return <ErrorState error={entities.error} onRetry={entities.reload} />

  return (
    <ImportWizardContext.Provider value={value}>
      <Outlet />
    </ImportWizardContext.Provider>
  )
}
